'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  GraduationCap,
  Bell,
  Settings,
  HelpCircle,
  Bookmark,
} from 'lucide-react';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: <LayoutDashboard size={18} />, href: '/learner-dashboard', group: 'main' },
  { label: 'Courses', icon: <BookOpen size={18} />, href: '/course-catalogue', group: 'main' },
  { label: 'My Learning', icon: <GraduationCap size={18} />, href: '/lesson-player', group: 'main' },
  { label: 'Bookmarks', icon: <Bookmark size={18} />, href: '/course-catalogue', group: 'main' },
  { label: 'Community', icon: <Users size={18} />, href: '/community-hub', badge: 3, group: 'community' },
  { label: 'Notifications', icon: <Bell size={18} />, href: '/learner-dashboard', badge: 5, group: 'community' },
  { label: 'Settings', icon: <Settings size={18} />, href: '/learner-dashboard', group: 'support' },
  { label: 'Help & Support', icon: <HelpCircle size={18} />, href: '/learner-dashboard', group: 'support' },
];

const groupLabels: Record<string, string> = {
  main: 'Learning',
  community: 'Community',
  support: 'Support',
};

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onCollapse: () => void;
  onMobileClose: () => void;
  activeRoute?: string;
}

export default function Sidebar({ collapsed, mobileOpen, onCollapse, onMobileClose, activeRoute }: SidebarProps) {
  const groups = ['main', 'community', 'support'];

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col fixed top-0 left-0 h-full bg-card border-r border-border z-30 sidebar-transition overflow-hidden
          ${collapsed ? 'w-16' : 'w-60'}
        `}
      >
        {/* Logo */}
        <div className={`flex items-center h-16 border-b border-border px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <Link href="/" className="flex items-center gap-2">
              <AppLogo size={32} />
              <span className="font-bold text-lg text-foreground tracking-tight">EugeneTechRN</span>
            </Link>
          )}
          {collapsed && (
            <Link href="/">
              <AppLogo size={32} />
            </Link>
          )}
          {!collapsed && (
            <button
              onClick={onCollapse}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
          {groups.map((group) => {
            const items = navItems.filter((n) => n.group === group);
            return (
              <div key={`group-${group}`} className="mb-4">
                {!collapsed && (
                  <p className="section-label px-3 mb-2">{groupLabels[group]}</p>
                )}
                {items.map((item) => {
                  const isActive = activeRoute === item.href || (item.href === '/learner-dashboard' && activeRoute === '/');
                  return (
                    <Link
                      key={`nav-${item.href}-${item.label}`}
                      href={item.href}
                      className={`
                        group relative flex items-center mb-1 transition-all duration-150
                        ${collapsed ? 'justify-center px-0 py-2.5 rounded-xl hover:bg-muted' : isActive ? 'nav-item-active' : 'nav-item'}
                      `}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className={isActive && !collapsed ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}>
                        {item.icon}
                      </span>
                      {!collapsed && <span className="flex-1">{item.label}</span>}
                      {!collapsed && item.badge && (
                        <span className="ml-auto text-xs font-semibold bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 leading-none">
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                      )}
                      {/* Tooltip for collapsed */}
                      {collapsed && (
                        <span className="absolute left-full ml-3 px-2.5 py-1.5 bg-foreground text-card text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-modal">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="p-2 border-t border-border">
            <button
              onClick={onCollapse}
              className="w-full flex items-center justify-center p-2 rounded-xl hover:bg-muted text-muted-foreground transition-colors"
              aria-label="Expand sidebar"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* User footer */}
        {!collapsed && (
          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted cursor-pointer transition-colors">
              <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                SA
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">Sophia Anderson</p>
                <p className="text-xs text-muted-foreground truncate">sophia@eugenetechrn.io</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-72 bg-card border-r border-border z-50 flex flex-col
          transition-transform duration-300 ease-in-out lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between h-16 border-b border-border px-4">
          <Link href="/" className="flex items-center gap-2" onClick={onMobileClose}>
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground tracking-tight">EugeneTechRN</span>
          </Link>
          <button
            onClick={onMobileClose}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {groups.map((group) => {
            const items = navItems.filter((n) => n.group === group);
            return (
              <div key={`mobile-group-${group}`} className="mb-4">
                <p className="section-label px-3 mb-2">{groupLabels[group]}</p>
                {items.map((item) => {
                  const isActive = activeRoute === item.href;
                  return (
                    <Link
                      key={`mobile-nav-${item.href}-${item.label}`}
                      href={item.href}
                      onClick={onMobileClose}
                      className={`flex items-center gap-3 mb-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${isActive ? 'bg-secondary text-primary font-semibold' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                    >
                      {item.icon}
                      <span className="flex-1">{item.label}</span>
                      {item.badge && (
                        <span className="text-xs font-semibold bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 leading-none">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 p-2 rounded-xl">
            <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold">
              SA
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Sophia Anderson</p>
              <p className="text-xs text-muted-foreground">sophia@eugenetechrn.io</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Spacer for desktop layout */}
      <div
        className={`hidden lg:block flex-shrink-0 sidebar-transition ${collapsed ? 'w-16' : 'w-60'}`}
      />
    </>
  );
}